import type { Day, Expense, TripConfig } from '../types';
import { doc, type DocumentReference, type Firestore, type Transaction } from 'firebase/firestore';

export type ExpenseSourceType = 'transport' | 'accommodation' | 'attraction' | 'food' | 'nightlife' | 'shopping' | 'other';
export type ExpenseLinkTarget = { sourceId: string; sourceType: ExpenseSourceType; date?: string; label: string };

/** Back-reference audit also catches old charges missing from the item's link list. */
export function assertSingleLegacyCharge(expense: Expense, expenses: Expense[]): void {
  const type = (value?: string) => value === 'note' ? 'other' : value;
  if (!expense.sourceId || expenses.some(other => other.id !== expense.id && other.sourceId === expense.sourceId
    && type(other.sourceType) === type(expense.sourceType))) {
    throw new Error('This booking has multiple historical charges. Review them together before converting to one full cost.');
  }
}

export function sourceFieldForType(type: ExpenseSourceType): keyof Day {
  return { transport: 'transports', accommodation: 'accommodations', attraction: 'attractions',
    food: 'eats', nightlife: 'nightlife', shopping: 'shopping', other: 'notes' }[type] as keyof Day;
}

/** Freeze this with the expense form before explicitly reviewing legacy accounting. */
export function expenseSourceItem(trip: TripConfig | null, expense: Expense | null): Record<string, any> | undefined {
  if (!trip || !expense?.sourceId || expense.itineraryDeleted) return;
  const field = sourceFieldForType((expense.sourceType === 'note' ? 'other' : expense.sourceType) as ExpenseSourceType);
  if (!field) return;
  const candidates = [
    ...(expense.sourceType === 'accommodation' ? trip.accommodations || [] : []),
    ...Object.values(trip.itinerary || {}).flatMap(day => [
      ...(Array.isArray(day[field]) ? day[field] as { id?: string; expenseLogId?: string }[] : []),
      ...(expense.sourceType === 'accommodation' && day.accommodation ? [day.accommodation] : []),
    ]),
  ];
  return candidates.find(item => item.id === expense.sourceId && item.expenseLogId === expense.id);
}

/** Extra charges are separate ledger rows; the primary expenseLogId is handled by existing writers. */
export async function secondaryChargeRefs(transaction: Transaction, db: Firestore, tripId: string,
  item: { id?: string; expenseLogId?: string; expenseLogIds?: string[] }, sourceType: ExpenseSourceType): Promise<DocumentReference[]> {
  const refs = [...new Set((item.expenseLogIds || []).filter(id => id !== item.expenseLogId))]
    .map(id => doc(db, 'trips', tripId, 'expenses', id));
  const snapshots = await Promise.all(refs.map(ref => transaction.get(ref)));
  return refs.filter((_, index) => {
    const snapshot = snapshots[index];
    if (!snapshot.exists()) return false; // An expense can have been deleted separately.
    const saved = snapshot.data();
    if (saved.sourceId !== item.id || (saved.sourceType !== sourceType
      && !(sourceType === 'other' && saved.sourceType === 'note'))) {
      throw new Error('A linked charge changed. Reopen this item before removing it.');
    }
    return true;
  });
}

/** One choice per item even when destination-wide Shortlist arrays repeat on several days. */
export function expenseLinkTargets(trip: TripConfig): ExpenseLinkTarget[] {
  const targets: ExpenseLinkTarget[] = [];
  const seen = new Set<string>();
  const add = (sourceType: ExpenseSourceType, sourceId: string | undefined, label: string | undefined, date?: string) => {
    if (!sourceId || !label || seen.has(`${sourceType}:${sourceId}`)) return;
    seen.add(`${sourceType}:${sourceId}`);
    targets.push({ sourceType, sourceId, label, ...(date ? { date } : {}) });
  };
  for (const stay of trip.accommodations || []) add('accommodation', stay.id, stay.name);
  // Firestore day documents are partial; their record key is the stored date.
  for (const [date, day] of Object.entries(trip.itinerary || {}).sort(([a], [b]) => a.localeCompare(b))) {
    const suffix = day.city ? ` · ${day.city}` : '';
    for (const leg of day.transports || []) add('transport', leg.id, leg.isRental ? `Car hire${leg.carrier ? ` · ${leg.carrier}` : ''}` : `${leg.mode || 'Transport'}: ${leg.from} → ${leg.to}`, date);
    for (const stay of day.accommodations || []) add('accommodation', stay.id, stay.name, date);
    if (day.accommodation?.name) add('accommodation', day.accommodation.id, day.accommodation.name, date);
    for (const [type, items] of [
      ['attraction', day.attractions], ['food', day.eats], ['nightlife', day.nightlife],
      ['shopping', day.shopping], ['other', day.notes],
    ] as const) for (const item of items || []) add(type, item.id, `${item.name}${suffix}`, date);
  }
  return targets;
}
